import {
  Box,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
  Skeleton,
  IconButton,
  Backdrop,
  CircularProgress,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { useThemes } from '../../../components/Theme/theme';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { setDbDateRange } from '../../../redux/reducers/DashboardSlice';

import { MoneyFormatv3 } from '../../../library/MoneyFormat';
import { useClosingStockSummary } from '../Hooks/useClosingStockSummary';
import { STOCK_SUMMARY_COLUMNS } from '../constants/TableHeaderColumns';
import ActionItems from '../../../pages/NewFigma/newReports/actionItems';
import { useSelector } from 'react-redux';
import AddNewItemModal from '../../../pages/PrimeFigma/Modals/AddNewItemModal';
import { setSelectedItem, clearSelectedItem } from '../../../redux/reducers/inventoryItemSelectionSlice';
import { StockItem, StockReportData, ItemDetails } from '../types';
import StockItemWiseSummary from './StockItemWiseSummary';

interface ClosingStockSummaryProps {
  variant?: 'page' | 'modal';
  includeOpeningBalance?: boolean;
}

function ClosingStockSummary({ variant = 'page', includeOpeningBalance = true }: ClosingStockSummaryProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [itemModal, setItemModal] = useState<boolean>(false);
  const [itemDetails, setItemDetails] = useState<ItemDetails | null>(null);
  const [stockReportModal, setStockReportModal] = useState<boolean>(false);
  const [stockReportData, setStockReportData] = useState<StockReportData | null>(null);
  const { theme } = useThemes();
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    page,
    rowsPerPage,
    displayedRows,
    totalRows,
    totals,
    isLoading,
    handleChangePage,
    handleChangeRowsPerPage,
    handleNavigateItemwiseView,
  } = useClosingStockSummary(includeOpeningBalance);

  useEffect(() => {
    if (location.state?.startTimeStamp && location.state?.endTimeStamp) {
      const range = {
        startTimeStamp: location.state.startTimeStamp,
        endTimeStamp: location.state.endTimeStamp,
      };
      dispatch(
        setDbDateRange({
          outerKey: 'report',
          innerKey: 'range',
          value: range,
        })
      );
    }
  }, [location.state, dispatch]);

  const { report } = useSelector((state: any) => state?.DashboardDataReducer) || {};
  const companyDetails = useSelector((state: any) => state?.companyReducer?.companyDetails);
  const companyRegistrationType = companyDetails?.relatedcompanydetailsnew?.[0]?.registrationtype;

  // Filter out HSN and Taxability columns if company registration type is 1 (Unregistered)
  const filteredColumns = useMemo(() => {
    return companyRegistrationType === 1
      ? STOCK_SUMMARY_COLUMNS.filter((column) => column.id !== 'HSN' && column.id !== 'taxability')
      : STOCK_SUMMARY_COLUMNS;
  }, [companyRegistrationType]);

  const handleItemClick = (itemData: StockItem): void => {
    setItemDetails({
      itemId: itemData.id,
      name: itemData.itemName,
      type: 'GOODS',
      view: true,
    });
    setItemModal(true);
  };

  const handleClosingStockClick = (itemData: StockItem): void => {
    setStockReportData({
      itemid: itemData.id,
      itemname: itemData.itemName,
      itemcode: itemData.itemCode,
      unit: itemData.unit || (itemData as any).measurementunit || 'N/A',
    });

    dispatch(
      setSelectedItem({
        itemid: itemData.id,
        itemname: itemData.itemName,
        itemcode: itemData.itemCode,
        unit: itemData.unit,
      })
    );

    const range = {
      startTimeStamp: location.state?.startTimeStamp || null,
      endTimeStamp: location.state?.endTimeStamp || null,
    };
    dispatch(
      setDbDateRange({
        outerKey: 'report',
        innerKey: 'range',
        value: range,
      })
    );

    setTimeout(() => {
      setStockReportModal(true);
    }, 100);
  };

  return (
    <Box className="PageHeight noTableCellBorder">
      {/* Loading Backdrop */}
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
        }}
        open={isLoading}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress color="inherit" />
          <Typography variant="h6" sx={{ color: 'white' }}>
            Loading stock data...
          </Typography>
        </Box>
      </Backdrop>

      {/* HEADER BAR */}
      <Box
        sx={{
          p: { xs: '5px 5px', sm: '1px 1px' },
          mb: 0.2,
          border: '1px solid rgba(255, 255, 255, 0.2)',
          backdropFilter: 'blur(10px)',
          position: 'relative',
        }}
      >
        {/* Header Row - Title and Totals Inline */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            mb: 0,
            p: 0,
            flexWrap: 'wrap',
          }}
        >
          {/* Left Side - Title and Search */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: 18,
                color: '#000000',
                textShadow: 'none',
              }}
            >
              Stock & Item wise Profit & Loss Report
            </Typography>
            {variant === 'page' && (
              <Stack direction="row" alignItems="center" justifyContent="center">
                <ActionItems
                  anchorEl={anchorEl}
                  setAnchorEl={setAnchorEl}
                  title="Period: "
                  page="report"
                  outstandingPeriod={false}
                  isCustomDateModalOpen={false}
                  setIsCustomDateModalOpen={() => {}}
                  includeOpeningBalance={false}
                  setIncludeOpeningBalance={() => {}}
                />
              </Stack>
            )}
          </Box>

          {/* Right Side - Totals */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <Box
              sx={{
                backgroundColor: 'transparent',
                // border: '1px solid #e2e8f0',
                // borderRadius: '12px',
                px: 1,
                py: 1,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  color: '#000000',
                  fontSize: 16,
                }}
              >
                {isLoading ? <Skeleton width={150} /> : `Closing Stock: ₹${MoneyFormatv3(totals?.closingStockAmount)}`}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      <Paper
        sx={{
          width: '100%',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          height: variant === 'modal' ? 'auto' : 'auto',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          backdropFilter: 'blur(10px)',
          boxShadow: 'none',
        }}
      >
        <TableContainer
          className="custom-scrollbar"
          sx={{
            height: {
              sm: 'calc( 100vh - 230px )',
              xs: 'calc( 100vh - 180px )',
            },
            flex: 1,
            overflowX: 'auto',
            overflowY: 'auto',
          }}
        >
          <Table
            stickyHeader
            aria-label="stock-table"
            size="small"
            sx={{
              minWidth: 1200,
              tableLayout: 'auto',
              borderCollapse: 'separate',
              borderSpacing: 0,
              '& th, & td': {
                borderRight: 'none',
                whiteSpace: 'nowrap',
              },
              '& th:last-of-type, & td:last-of-type': { borderRight: 'none' },
              '& thead': {
                position: 'sticky',
                top: 0,
                zIndex: 3,
                backgroundColor: '#fff',
              },
              '& tbody': {
                zIndex: 1,
              },
              '& thead tr:first-of-type': {
                '& th': {
                  borderBottom: 'none !important',
                },
              },
            }}
          >
            <TableHead>
              <TableRow
                sx={{
                  '.MuiTableCell-root': {
                    backgroundColor: 'background.tablebg !important',
                    fontSize: '14px',
                    fontWeight: 600,
                    padding: '4px 8px',
                    borderBottom: 'none !important',
                    borderLeft: 'none',
                    borderRight: 'none',
                    position: 'relative',
                    zIndex: 3,
                  },
                }}
              >
                {filteredColumns.map((column) => (
                  <TableCell
                    key={column.id}
                    align="center"
                    style={{
                      minWidth: column.minWidth,
                      fontSize: 14,
                    }}
                    colSpan={column.subColumns ? column.subColumns.length : 1}
                  >
                    {column.id === 'pandl' ? (
                      <>
                        <span style={{ color: 'green' }}>Profit</span>/<span style={{ color: 'red' }}>Loss</span>
                      </>
                    ) : (
                      column.label
                    )}{' '}
                  </TableCell>
                ))}
              </TableRow>

              <TableRow
                sx={{
                  '.MuiTableCell-root': {
                    backgroundColor: '#fff',
                    fontSize: '14px',
                    padding: '4px 8px',
                    fontWeight: 800,
                    borderRight: 'none',
                    borderBottom: 'none',
                    position: 'relative',
                    zIndex: 1,
                    color: '#000000',
                  },
                }}
              >
                {filteredColumns.flatMap((column) =>
                  column.subColumns
                    ? column.subColumns.map((subColumn) => (
                        <TableCell
                          key={subColumn.id}
                          align="center"
                          style={{
                            minWidth: subColumn.minWidth,
                            fontSize: 14,
                            color: '#000000',
                          }}
                        >
                          {subColumn.label}
                        </TableCell>
                      ))
                    : [<TableCell key={column.id} />]
                )}
              </TableRow>
            </TableHead>

            <TableBody>
              {isLoading
                ? Array.from(new Array(5)).map((_, index) => (
                    <TableRow key={index}>
                      {filteredColumns.flatMap((column) =>
                        column.subColumns ? (
                          column.subColumns.map((subColumn) => (
                            <TableCell key={subColumn.id}>
                              <Skeleton />
                            </TableCell>
                          ))
                        ) : (
                          <TableCell key={column.id}>
                            <Skeleton />
                          </TableCell>
                        )
                      )}
                    </TableRow>
                  ))
                : displayedRows.map((row, index) => (
                    <TableRow
                      hover
                      role="checkbox"
                      tabIndex={-1}
                      key={index}
                      sx={{
                        '&:hover': { backgroundColor: '#f8fafc' },
                        '.MuiTableCell-root': {
                          padding: '6px 8px',
                          borderBottom: 'none',
                          borderRight: 'none',
                        },
                        '.MuiTableCell-root:last-child': {
                          borderRight: 'none',
                        },
                      }}
                    >
                      {filteredColumns.map((column) => {
                        if (column.subColumns) {
                          return column.subColumns.map((subColumn) => {
                            const isClosingStockColumn =
                              column.id === 'closingStock' && (subColumn.id === 'closingStockQty' || subColumn.id === 'closingStockAmount');

                            if (isClosingStockColumn) {
                              return (
                                <Tooltip title="Click to view Item Stock Ledger report" placement="bottom" key={subColumn.id}>
                                  <TableCell
                                    align={subColumn.align}
                                    style={{
                                      fontSize: 13,
                                      minWidth: subColumn.minWidth,
                                    }}
                                    sx={{
                                      cursor: 'pointer',
                                      color: '#2196f3 !important',
                                      textDecoration: 'underline',
                                      textDecorationColor: '#2196f3',
                                      '&:hover': {
                                        backgroundColor: '#f5f5f5',
                                        color: '#1976d2 !important',
                                      },
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleClosingStockClick(row);
                                    }}
                                  >
                                    {subColumn.label.includes('Amt')
                                      ? `₹${MoneyFormatv3((row as any)[subColumn.id])}`
                                      : (row as any)[subColumn.id]}
                                  </TableCell>
                                </Tooltip>
                              );
                            }

                            return (
                              <TableCell
                                key={subColumn.id}
                                align={subColumn.align}
                                style={{
                                  fontSize: 13,
                                  minWidth: subColumn.minWidth,
                                }}
                                sx={{
                                  color: '#000000',
                                }}
                              >
                                {subColumn.label.includes('Amt') ? `₹${MoneyFormatv3((row as any)[subColumn.id])}` : (row as any)[subColumn.id]}
                              </TableCell>
                            );
                          });
                        }
                        if (column.id === 'itemCode' || column.id === 'itemName') {
                          return (
                            <Tooltip
                              title={`Click to view ${column.id === 'itemCode' ? 'item details' : 'item details'}`}
                              placement="bottom"
                            >
                              <TableCell
                                key={column.id}
                                align={column.align}
                                sx={{
                                  minWidth: column.minWidth,
                                  fontSize: 13,
                                  cursor: 'pointer',
                                  color: '#2196f3 !important',
                                  textDecoration: 'underline',
                                  textDecorationColor: '#2196f3',
                                  '&:hover': {
                                    backgroundColor: '#f5f5f5',
                                    color: '#1976d2 !important',
                                  },
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleItemClick(row);
                                }}
                              >
                                {(row as any)[column.id]}
                              </TableCell>
                            </Tooltip>
                          );
                        }

                        return (
                          <Tooltip
                            title={column.id === 'closingStock' ? `₹${MoneyFormatv3((row as any)[column.id])}` : (row as any)[column.id]}
                            placement="bottom"
                          >
                            <TableCell
                              key={column.id}
                              align={column.align}
                              sx={{
                                minWidth: column.minWidth,
                                fontSize: 13,
                                cursor: 'default',
                                fontWeight: column.id === 'margin' || column.id === 'pandl' ? 'bold' : 'normal',
                                color:
                                  (column.id === 'margin' || column.id === 'pandl') && (row as any)[column.id] !== 0
                                    ? (row as any)[column.id] < 0
                                      ? 'red'
                                      : 'green'
                                    : '#000000',
                              }}
                            >
                              {column.id === 'closingStock' || column.id === 'cogs' || column.id === 'pandl'
                                ? `₹${MoneyFormatv3((row as any)[column.id])}`
                                : (row as any)[column.id]}
                            </TableCell>
                          </Tooltip>
                        );
                      })}
                    </TableRow>
                  ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={totalRows}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            borderTop: 'none',
            '.MuiTablePagination-select': {
              paddingY: '8px',
            },
          }}
        />
      </Paper>

      {/* Item Modal */}
      {itemModal && itemDetails && (
        <AddNewItemModal
          open={itemModal}
          setOpen={setItemModal}
          modalEvent={true}
          itemID={itemDetails.itemId}
          setItemID={() => {}}
          categoryId=""
          itemName={itemDetails.name}
          itemType={itemDetails.type}
          page=""
          getItems={() => {}}
          invDate=""
          setAccNames={() => {}}
          view={itemDetails.view}
          rowIndex={0}
          listItems={[]}
        />
      )}

      {/* Stock Report Modal */}
      {stockReportModal && stockReportData && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            // Responsive padding so gaps stay equal on all sides across breakpoints
            p: { xs: 1, sm: 2, md: 3 },
          }}
        >
          <Box
            sx={{
              backgroundColor: '#fff',
              borderRadius: '12px',
              // Match centering behaviour used in ItemNewTable stock ledger modal
              width: {
                xs: 'calc(100vw - 20px)',
                sm: 'calc(100vw - 40px)',
                md: 'calc(100vw - 60px)',
                lg: 'calc(100vw - 80px)',
                xl: 'calc(100vw - 100px)',
              },
              maxWidth: {
                xs: 'calc(100vw - 20px)',
                sm: 'calc(100vw - 40px)',
                md: 'calc(100vw - 60px)',
                lg: '1800px',
              },
              height: {
                xs: 'calc(100vh - 20px)',
                sm: 'calc(100vh - 40px)',
                md: 'calc(100vh - 60px)',
                lg: 'calc(100vh - 80px)',
                xl: 'calc(100vh - 100px)',
              },
              maxHeight: {
                xs: 'calc(100vh - 20px)',
                sm: 'calc(100vh - 40px)',
                md: 'calc(100vh - 60px)',
                lg: 'calc(100vh - 80px)',
                xl: 'calc(100vh - 100px)',
              },
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              margin: 'auto',
            }}
          >
            {/* Modal Header */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '16px 20px',
                borderBottom: '1px solid #e2e8f0',
                backgroundColor: '#f8fafc',
              }}
            >
              <Typography sx={{ fontSize: '18px', fontWeight: 600 }}>
                Item Stock Ledger Report (FIFO Method) - {stockReportData.itemname}
              </Typography>
              <Tooltip title="Close">
                <IconButton
                  onClick={() => {
                    setStockReportModal(false);
                    setStockReportData(null);
                  }}
                  sx={{
                    color: '#64748b',
                    '&:hover': { backgroundColor: '#e2e8f0' },
                  }}
                >
                  <CloseRoundedIcon />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Modal Content */}
            <Box
              sx={{
                flex: 1,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                padding: { xs: '16px', sm: '20px', md: '20px' },
              }}
            >
              <StockItemWiseSummary
                key={`modal-${stockReportData?.itemid}`}
                itemDetails={stockReportData}
                variant="modal"
                includeOpeningBalance={includeOpeningBalance}
              />
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default ClosingStockSummary;
